import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Procedure } from '@medplum/fhirtypes';
import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/date';
import { Item, ItemActions, ItemContent, ItemDescription, ItemFooter, ItemTitle } from '../ui/item';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type ProceduresSummaryProps = {
  procedures: WithId<Procedure>[];
  onViewAll?: () => void;
  onSelectProcedure?: (procedureId: string) => void;
  selectedProcedureId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

export const ProceduresSummary = ({
  procedures,
  onViewAll,
  onSelectProcedure,
  selectedProcedureId,
  onPromotionClick,
  getPromotionStatus,
}: ProceduresSummaryProps): React.ReactElement | null => {
  const { t } = useTranslation('records');

  if (onViewAll && procedures.length === 0) {
    return null;
  }

  // If onViewAll is provided, show a single summary card
  if (onViewAll && !onSelectProcedure) {
    const totalProcedures = procedures.length;
    const mostRecent = procedures[0];
    const mostRecentDate = mostRecent?.performedDateTime ?? mostRecent?.performedPeriod?.start;

    return (
      <Item className="cursor-pointer" onClick={onViewAll}>
        <ItemContent>
          <ItemTitle className="text-lg font-medium">{t('proceduresAndExams')}</ItemTitle>
          <ItemDescription className="text-xs text-muted-foreground">
            {totalProcedures} {t('onRecord')}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRightIcon className="size-4 text-muted-foreground" />
        </ItemActions>
        {mostRecent && (
          <ItemFooter className="justify-start text-xs">
            <span className="text-muted-foreground">{t('mostRecent')}</span>
            <span className="font-medium">{formatCodeableConcept(mostRecent.code) || t('unknownProcedure')}</span>
            <span className="text-muted-foreground">
              {' '}
              {t('onRecordDate', {
                date: mostRecentDate ? formatDate(mostRecentDate) : t('unknown'),
              })}
            </span>
          </ItemFooter>
        )}
      </Item>
    );
  }

  // Show individual procedure cards
  return (
    <div className="space-y-2">
      {procedures.map((procedure) => {
        const name = formatCodeableConcept(procedure.code) || t('unknownProcedure');
        const performedDateTime = procedure.performedDateTime ?? procedure.performedPeriod?.start;
        const date = performedDateTime ? formatDate(performedDateTime) : t('unknown');
        const status = procedure.status || t('unknown');
        const category = formatCodeableConcept(procedure.category) || null;
        const indication = formatCodeableConcept(procedure.reasonCode?.at(0)) || null;
        const performedBy = procedure.performer?.at(0)?.actor?.display || null;

        const description = [
          status,
          category,
          indication && `${t('indicationLabel')} ${indication}`,
          performedBy && `${t('performedByLabel')} ${performedBy}`,
          `${t('dateLabel')} ${date}`,
        ]
          .filter(Boolean)
          .join(' · ');

        return (
          <RecordListItem
            key={procedure.id}
            title={name}
            description={description}
            onClick={onSelectProcedure ? (): void => onSelectProcedure(procedure.id) : undefined}
            selected={selectedProcedureId === procedure.id}
            onPromotionClick={onPromotionClick ? (): void => onPromotionClick(procedure.id) : undefined}
            promotionStatus={getPromotionStatus?.(procedure.id)}
          />
        );
      })}
    </div>
  );
};
